import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Play, Folder, Download, AlertTriangle, CheckCircle2, Pause,
  FileText, Loader2, RefreshCw, ArrowRight, Info,
} from 'lucide-react';
import { API } from '@/App';

const SUB_TABS = [
  { key: 'leaf',    label: 'Leaf & Canopy' },
  { key: 'seed',    label: 'Seed & Fruit' },
  { key: 'disease', label: 'Disease & Lesion' },
  { key: 'panicle', label: 'Panicle / Spike' },
  { key: 'root',    label: 'Root Analysis' },
];

export default function Batch() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [subTab, setSubTab] = useState('leaf');
  const [regex, setRegex] = useState('');
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [regexPreview, setRegexPreview] = useState([]);
  const [busy, setBusy] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const cfgReq = { withCredentials: true };

  const setB = (k, v) => setBusy(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/phenoscan/batch/config`, cfgReq)
      .then(r => { setCfg(r.data); if (!regex) setRegex(r.data.default_regex); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll status while running
  useEffect(() => {
    if (!job || !status || !['running', 'paused'].includes(status.status)) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/phenoscan/batch/${job.job_id}/status`, cfgReq);
        setStatus(r.data);
        if (r.data.status === 'done' || r.data.status === 'error') {
          const res = await axios.get(`${API}/phenoscan/batch/${job.job_id}/results`, cfgReq);
          setResults(res.data);
          clearInterval(t);
        }
      } catch (e) {
        // Polling tick failed (transient network or 404 if job purged).
        // Log to devtools but don't stop the interval — it'll retry.
        // eslint-disable-next-line no-console
        console.warn('phenoscan Batch polling failed:', e?.message || e);
      }
    }, 1500);
    return () => clearInterval(t);
  }, [job, status]);

  // ─────────── Create job ───────────
  const createJob = async () => {
    setB('create', true);
    try {
      const r = await axios.post(`${API}/phenoscan/batch/create`,
        { sub_tab: subTab, regex, params: { aruco_cm: 3.0, coin_cm: 2.3 } }, cfgReq);
      setJob(r.data.job);
      setStatus({ ...r.data.job, total: 0, done: 0 });
      setResults(null);
      toast.success('Batch job created');
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('create', false); }
  };

  // ─────────── Add files ───────────
  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!job) { toast.error('Create a batch job first'); return; }
    if (files.length > 500) {
      toast.warning(`${files.length} files — very large. Consider splitting.`);
    }
    setB('upload', true);
    try {
      // Send in chunks of 50
      const CHUNK = 50;
      for (let i = 0; i < files.length; i += CHUNK) {
        const fd = new FormData();
        files.slice(i, i + CHUNK).forEach(f => fd.append('files', f));
        await axios.post(`${API}/phenoscan/batch/${job.job_id}/add-images`, fd,
          { ...cfgReq, headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const st = await axios.get(`${API}/phenoscan/batch/${job.job_id}/status`, cfgReq);
      setStatus(st.data);
      // Refresh regex preview using the first 3 filenames
      refreshRegexPreview(files.slice(0, 3).map(f => f.name));
      toast.success(`${files.length} files added`);
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('upload', false); e.target.value = ''; }
  };

  // ─────────── Validation folder ───────────
  const loadValidationFolder = async () => {
    if (!job) { toast.error('Create a Leaf job first'); return; }
    if (job.sub_tab !== 'leaf') {
      toast.error('Validation folder is leaf-only');
      return;
    }
    setB('valfolder', true);
    try {
      const r = await axios.post(`${API}/phenoscan/batch/${job.job_id}/load-validation-folder`,
        {}, cfgReq);
      const st = await axios.get(`${API}/phenoscan/batch/${job.job_id}/status`, cfgReq);
      setStatus(st.data);
      const meta = await axios.get(`${API}/phenoscan/batch/validation-folder/meta`, cfgReq);
      refreshRegexPreview(meta.data.ground_truth.slice(0, 3).map(g => g.filename));
      toast.success(`Loaded ${r.data.added} validation images`);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('valfolder', false); }
  };

  // ─────────── Regex live preview ───────────
  const refreshRegexPreview = useCallback(async (filenames) => {
    if (!filenames || !filenames.length) return;
    try {
      const r = await axios.post(`${API}/phenoscan/batch/preview-regex`,
        { filenames, regex }, cfgReq);
      setRegexPreview(r.data.preview);
    } catch (e) {
      // Regex preview is non-critical — log and continue.
      // eslint-disable-next-line no-console
      console.warn('phenoscan regex preview failed:', e?.message || e);
    }
  }, [regex]);

  const applyRegex = async () => {
    if (!job) return;
    try {
      const r = await axios.post(`${API}/phenoscan/batch/${job.job_id}/update-regex`,
        { regex }, cfgReq);
      setRegexPreview(r.data.preview);
      const st = await axios.get(`${API}/phenoscan/batch/${job.job_id}/status`, cfgReq);
      setStatus(st.data);
      toast.success('Regex updated');
    } catch (e) {
      toast.error(`Update failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // ─────────── Start / Pause / Resume ───────────
  const startJob = async () => {
    if (!job || !status || status.total === 0) {
      toast.error('Add images first'); return;
    }
    // Show confirmation per spec
    const est = Math.ceil(status.total * 2 / 60);   // ~2 s/image estimate
    if (status.total >= 30) {
      setConfirmModal({
        total: status.total, est,
        onOk: () => { setConfirmModal(null); doStart(); },
        onCancel: () => setConfirmModal(null),
      });
      return;
    }
    doStart();
  };

  const doStart = async () => {
    setB('start', true);
    try {
      const r = await axios.post(`${API}/phenoscan/batch/${job.job_id}/start`, {}, cfgReq);
      setStatus(s => ({ ...s, status: r.data.status || 'running' }));
    } catch (e) {
      toast.error(`Start failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('start', false); }
  };

  const pauseJob = async () => {
    setB('pause', true);
    try { await axios.post(`${API}/phenoscan/batch/${job.job_id}/pause`, {}, cfgReq); }
    finally { setB('pause', false); }
  };
  const resumeJob = async () => {
    setB('resume', true);
    try {
      await axios.post(`${API}/phenoscan/batch/${job.job_id}/resume`, {}, cfgReq);
      setStatus(s => ({ ...s, status: 'running' }));
    } finally { setB('resume', false); }
  };

  // ─────────── Export / Handoff ───────────
  const exportFile = async (fmt) => {
    if (!job) return;
    try {
      const r = await axios.get(`${API}/phenoscan/batch/${job.job_id}/export?fmt=${fmt}`,
        { ...cfgReq, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_${job.job_id.slice(0, 8)}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const sendToIntegrated = async () => {
    if (!job || status?.status !== 'done') { toast.error('Batch must complete first'); return; }
    setB('handoff', true);
    try {
      const r = await axios.post(`${API}/phenoscan/batch/${job.job_id}/handoff-integrated`,
        {}, cfgReq);
      toast.success(`${r.data.rows_ingested} rows ingested into ${r.data.source}`);
      // Router-state: navigate to Integrated tab (no storage)
      navigate('/phenoscan?tab=integrated');
    } catch (e) {
      toast.error(`Hand-off failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('handoff', false); }
  };

  const copySettings = () => {
    navigator.clipboard?.writeText(JSON.stringify({
      module: 'phenoscan_batch', sub_tab: subTab, regex,
      params: { aruco_cm: 3.0, coin_cm: 2.3 }, version: 'bkagrid-phenoscan-batch-1.0',
    }, null, 2));
    toast.success('Settings copied');
  };

  const progressPct = useMemo(() =>
    status && status.total ? Math.round(100 * status.done / status.total) : 0,
    [status]);

  const unmatchedPct = useMemo(() => {
    if (!status || !status.total) return 0;
    return Math.round(100 * (status.parse_unmatched || 0) / status.total);
  }, [status]);

  const failedCalibration = useMemo(() => {
    if (!results) return { count: 0, pct: 0 };
    const n = results.images.filter(i => i.status === 'ok' && !i.calibrated).length;
    return { count: n, pct: results.total ? Math.round(100 * n / results.total) : 0 };
  }, [results]);

  return (
    <div data-testid="phenoscan-batch-pane" className="space-y-6">
      {/* ══════════ Setup ══════════ */}
      <Section icon={Folder} title="1. Batch setup"
        right={
          <button onClick={copySettings} data-testid="batch-copy-settings"
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
            style={{ background: 'white', border: '1px solid #E5E5E0', color: '#6F695F' }}>
            <FileText size={12} /> Copy settings
          </button>
        }>
        <div className="flex flex-wrap gap-4 items-center">
          <label className="flex items-center gap-2 text-xs" data-testid="batch-subtab-select">
            <span className="font-semibold">Analysis:</span>
            <select value={subTab} onChange={e => setSubTab(e.target.value)}
              disabled={!!job}
              className="px-2 py-1 rounded" style={{ border: '1px solid #E5E5E0' }}>
              {SUB_TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <button onClick={createJob} disabled={busy.create || !!job}
            data-testid="batch-create-btn"
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{ background: '#2D6A4F', color: 'white', opacity: job ? 0.5 : 1 }}>
            {busy.create ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Create batch job
          </button>
          {job && (
            <>
              <span className="text-[11px]" style={{ color: '#2D6A4F' }} data-testid="batch-job-id">
                job: <code>{job.job_id.slice(0, 12)}…</code> · sub-tab: <b>{job.sub_tab}</b>
              </span>
              <button onClick={() => { setJob(null); setStatus(null); setResults(null); setRegexPreview([]); }}
                data-testid="batch-clear-btn"
                className="text-[11px] px-2 py-1 rounded" style={{ border: '1px solid #E5E5E0' }}>
                <RefreshCw size={10} className="inline" /> New batch
              </button>
            </>
          )}
        </div>
      </Section>

      {/* ══════════ Upload ══════════ */}
      {job && (
        <Section icon={Upload} title="2. Upload images">
          <div className="flex flex-wrap gap-3 items-center mb-3">
            <input ref={fileRef} type="file" multiple accept="image/*"
              onChange={onFilesChosen} className="hidden"
              data-testid="batch-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={busy.upload}
              data-testid="batch-upload-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
              style={{ background: '#1B4332', color: 'white' }}>
              {busy.upload ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Choose files / folder
            </button>
            {job.sub_tab === 'leaf' && (
              <button onClick={loadValidationFolder} disabled={busy.valfolder}
                data-testid="batch-load-validation-btn"
                className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
                {busy.valfolder ? <Loader2 size={12} className="animate-spin" /> : <Folder size={12} />}
                Load validation folder (30 images, 10 genotypes × 3 reps)
              </button>
            )}
            {status && status.total > 0 && (
              <span className="text-xs" style={{ color: '#2D6A4F' }} data-testid="batch-total">
                <CheckCircle2 size={12} className="inline mr-1" />
                {status.total} image{status.total === 1 ? '' : 's'} uploaded
              </span>
            )}
          </div>

          {/* Regex editor with live preview */}
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #F3F6F3' }}>
            <div className="text-xs font-semibold mb-1" style={{ color: '#1B4332' }}>
              Filename pattern (regex with named groups: genotype, rep, block, treatment)
            </div>
            <div className="flex gap-2 items-center">
              <input type="text" value={regex} onChange={e => setRegex(e.target.value)}
                data-testid="batch-regex-input"
                className="px-2 py-1 rounded text-xs flex-1 font-mono"
                style={{ border: '1px solid #E5E5E0' }} />
              <button onClick={applyRegex} disabled={!job}
                data-testid="batch-regex-apply"
                className="px-3 py-1 rounded text-xs"
                style={{ background: '#F3F6F3', color: '#1B4332', border: '1px solid #D1D5C8' }}>
                Apply
              </button>
            </div>
            {regexPreview.length > 0 && (
              <div className="overflow-x-auto mt-2">
                <table className="text-[11px] w-full" data-testid="batch-regex-preview">
                  <thead><tr style={{ background: '#F3F6F3' }}>
                    <th className="text-left px-2 py-1">Filename</th>
                    <th className="text-left px-2 py-1">Match</th>
                    <th className="text-left px-2 py-1">Genotype</th>
                    <th className="text-left px-2 py-1">Rep</th>
                    <th className="text-left px-2 py-1">Block</th>
                    <th className="text-left px-2 py-1">Treatment</th>
                    <th className="text-left px-2 py-1">sample_id</th>
                  </tr></thead>
                  <tbody>
                    {regexPreview.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #F3F6F3' }}>
                        <td className="px-2 py-1 font-mono">{p.filename}</td>
                        <td className="px-2 py-1" style={{ color: p.matched ? '#2D6A4F' : '#C026D3' }}>
                          {p.matched ? '✓' : '✗'}
                        </td>
                        <td className="px-2 py-1 font-mono">{p.genotype || '—'}</td>
                        <td className="px-2 py-1 font-mono">{p.rep || '—'}</td>
                        <td className="px-2 py-1 font-mono">{p.block || '—'}</td>
                        <td className="px-2 py-1 font-mono">{p.treatment || '—'}</td>
                        <td className="px-2 py-1 font-mono">{p.sample_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {status && status.total > 0 && unmatchedPct > 0 && (
              <div className="text-[11px] mt-2 flex items-center gap-1.5" style={{ color: '#92400E' }}>
                <AlertTriangle size={11} />
                {status.parse_unmatched} / {status.total} filenames do not match the regex
                ({unmatchedPct}%). Images still processed; sample_id falls back to filename stem.
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ══════════ Run ══════════ */}
      {job && (
        <Section icon={Play} title="3. Run batch"
          right={
            <div className="flex gap-2">
              {status?.status === 'running' && (
                <button onClick={pauseJob} data-testid="batch-pause-btn"
                  className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                  style={{ background: 'white', border: '1px solid #92400E', color: '#92400E' }}>
                  <Pause size={12} /> Pause
                </button>
              )}
              {status?.status === 'paused' && (
                <button onClick={resumeJob} data-testid="batch-resume-btn"
                  className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                  style={{ background: '#2D6A4F', color: 'white' }}>
                  <Play size={12} /> Resume
                </button>
              )}
              {(!status || !['running', 'paused', 'done'].includes(status.status)) && (
                <button onClick={startJob} disabled={busy.start || !status?.total}
                  data-testid="batch-start-btn"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
                  style={{ background: '#2D6A4F', color: 'white',
                           opacity: !status?.total ? 0.5 : 1 }}>
                  {busy.start ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                  Start
                </button>
              )}
            </div>
          }>
          <div className="mb-2">
            <div className="flex justify-between text-xs mb-1">
              <span data-testid="batch-progress-label">
                {status?.status || 'not started'} — {status?.done || 0} / {status?.total || 0}
                {status && status.status === 'running' && ' · processing…'}
              </span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 rounded-full" style={{ background: '#E5E5E0' }}>
              <div className="h-2 rounded-full transition-all"
                style={{ background: '#2D6A4F', width: `${progressPct}%` }}
                data-testid="batch-progress-bar" />
            </div>
          </div>
          {status?.errors?.length > 0 && (
            <details className="mt-3" data-testid="batch-error-log">
              <summary className="text-xs cursor-pointer" style={{ color: '#C026D3' }}>
                {status.errors.length} image{status.errors.length === 1 ? '' : 's'} failed (click to view)
              </summary>
              <div className="text-[10px] mt-2 p-2 rounded max-h-40 overflow-auto"
                style={{ background: '#FEF3C7', color: '#78350F' }}>
                {status.errors.slice(0, 50).map((e, i) => (
                  <div key={i}><code>{e.filename}</code>: {e.error}</div>
                ))}
              </div>
            </details>
          )}
        </Section>
      )}

      {/* ══════════ Results ══════════ */}
      {results && results.status === 'done' && (
        <Section icon={CheckCircle2} title="4. Results"
          right={
            <div className="flex gap-2">
              <button onClick={() => exportFile('csv')}
                data-testid="batch-export-csv"
                className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
                <Download size={12} /> CSV (per image)
              </button>
              <button onClick={() => exportFile('xlsx')}
                data-testid="batch-export-xlsx"
                className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
                <Download size={12} /> XLSX (+ genotype summary)
              </button>
              <button onClick={sendToIntegrated} disabled={busy.handoff}
                data-testid="batch-handoff-integrated"
                className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
                style={{ background: '#1B4332', color: 'white' }}>
                <ArrowRight size={12} /> Send to Integrated Analysis
              </button>
            </div>
          }>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Stat label="Total" value={results.total} />
            <Stat label="OK" value={results.images.filter(i => i.status === 'ok').length} accent />
            <Stat label="Errors" value={results.errors.length} />
            <Stat label="Uncalibrated" value={failedCalibration.count} />
          </div>
          {failedCalibration.pct >= 50 && (
            <div className="rounded-lg p-3 mb-3 flex gap-2 items-start"
              style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}
              data-testid="batch-calibration-warning">
              <AlertTriangle size={14} style={{ color: '#92400E' }} />
              <div className="text-xs" style={{ color: '#78350F' }}>
                <b>{failedCalibration.pct}%</b> of images have no ArUco marker detected —
                numeric outputs are in pixel space (relative units).
              </div>
            </div>
          )}
          <div className="overflow-auto rounded border" style={{ borderColor: '#E5E5E0', maxHeight: 320 }}>
            <table className="text-[11px]" data-testid="batch-results-table">
              <thead><tr style={{ background: '#F3F6F3', position: 'sticky', top: 0 }}>
                {['filename', 'genotype', 'rep', 'status', 'calibrated',
                  ...Object.keys((results.images.find(i => i.traits) || { traits: {} }).traits).slice(0, 5)]
                  .map(c => <th key={c} className="px-2 py-1 text-left">{c}</th>)}
              </tr></thead>
              <tbody>
                {results.images.slice(0, 80).map((i, idx) => {
                  const sampleTraits = results.images.find(x => x.traits && Object.keys(x.traits).length) || { traits: {} };
                  const traitKeys = Object.keys(sampleTraits.traits).slice(0, 5);
                  return (
                    <tr key={idx} style={{ borderTop: '1px solid #F3F6F3' }}>
                      <td className="px-2 py-0.5 font-mono">{i.filename}</td>
                      <td className="px-2 py-0.5 font-mono">{i.genotype || '—'}</td>
                      <td className="px-2 py-0.5 font-mono">{i.rep || '—'}</td>
                      <td className="px-2 py-0.5" style={{ color: i.status === 'ok' ? '#2D6A4F' : '#C026D3' }}>
                        {i.status}
                      </td>
                      <td className="px-2 py-0.5">{i.calibrated ? '✓' : '—'}</td>
                      {traitKeys.map(k => (
                        <td key={k} className="px-2 py-0.5">
                          {i.traits?.[k] === null || i.traits?.[k] === undefined ? '—'
                            : typeof i.traits[k] === 'number'
                              ? i.traits[k].toFixed(3) : String(i.traits[k])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {results.images.length > 80 && (
              <div className="text-[10px] px-2 py-1" style={{ color: '#6F695F' }}>
                Showing first 80 of {results.images.length} rows.
              </div>
            )}
          </div>
          <div className="text-[11px] mt-2 flex items-center gap-1.5" style={{ color: '#6F695F' }}>
            <Info size={11} /> Batch results persist for 24 hours in your session.
            Hand-off to Integrated ingests the <b>{results.sub_tab}</b> source with {results.images.filter(i => i.status === 'ok').length} rows.
          </div>
        </Section>
      )}

      {/* Confirmation modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          data-testid="batch-confirm-modal">
          <div className="rounded-xl bg-white p-5 max-w-md mx-4" style={{ border: '1px solid #E5E5E0' }}>
            <div className="font-display text-lg mb-2" style={{ color: '#1B4332' }}>
              Confirm batch run
            </div>
            <p className="text-sm" style={{ color: '#4F4B44' }}>
              About to process <b>{confirmModal.total} images</b>.
              Estimated time: <b>~{confirmModal.est} min</b> at ~2 seconds per image.
              You may close the browser and return — progress is checkpointed every image.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={confirmModal.onCancel}
                data-testid="batch-confirm-cancel"
                className="px-3 py-1.5 rounded text-xs"
                style={{ border: '1px solid #E5E5E0' }}>Cancel</button>
              <button onClick={confirmModal.onOk}
                data-testid="batch-confirm-ok"
                className="px-3 py-1.5 rounded text-xs font-semibold"
                style={{ background: '#2D6A4F', color: 'white' }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="rounded-xl bg-white p-4" style={{ border: '1px solid #E5E5E0' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: '#2D6A4F' }} />
          <div className="font-display text-base" style={{ color: '#1B4332' }}>{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md p-2" style={{ background: accent ? '#D1FAE5' : '#F3F6F3' }}>
      <div className="text-[10px] uppercase" style={{ color: '#6F695F', letterSpacing: 0.5 }}>{label}</div>
      <div className="font-semibold" style={{ color: accent ? '#065F46' : '#1B4332', fontSize: 16 }}>
        {value}
      </div>
    </div>
  );
}
